/** @format */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { auditLogService } from '@services/auditLog';
import {
  ACTION_TYPE_LABELS,
  RESOURCE_CATEGORY_LABELS,
  RESOURCE_TYPE_LABELS,
  getResourceTypesByCategory,
} from '@constants/auditConstants';
import {
  AuditActionStatus,
  AuditActionType,
  AuditLogEntry,
  AuditLogSearchParams,
} from '../../types/auditLog';
import { getCurrentUserId } from '@utils/userUtils';
import {
  formatFacilityDropdownLabel,
  getSelectedFacilityFromSession,
} from '@utils/facilityUtils';
import { UserFacility } from '@services/facilityManagement';
import { getSelectedCountryCode } from '@utils/countryUtils';
import { getApiOrigin } from '@utils/apiOrigin';

function getValidAccessToken(): string | null {
  const token = localStorage.getItem('access_token');
  const expStr = localStorage.getItem('access_token_expires_at');
  if (!token) return null;
  if (!expStr) return token;
  const exp = Number(expStr);
  if (!Number.isFinite(exp)) return token;
  return Date.now() < exp ? token : null;
}

async function getFacilitiesForAuditLog(userId: number): Promise<UserFacility[]> {
  // getApiOrigin() is always configured in deployed Hub builds (QA/PD/CI).
  const API_BASE_URL = getApiOrigin();
  const accessToken = getValidAccessToken();
  if (!accessToken) {
    throw new Error('Not authenticated or token expired. Please sign in again.');
  }

  const url = `${API_BASE_URL}/api/v1/permissions/user/${userId}/facilities/audit-log`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const text = await response.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error('Invalid JSON response from server');
  }

  if (!response.ok) {
    const errorMessage = json?.message || json?.error || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  if (Array.isArray(json)) return json;
  if (json?.data?.facilities && Array.isArray(json.data.facilities)) return json.data.facilities;
  if (json?.facilities && Array.isArray(json.facilities)) return json.facilities;
  if (json?.data && Array.isArray(json.data)) return json.data;
  if (json?.results && Array.isArray(json.results)) return json.results;
  if (json?.items && Array.isArray(json.items)) return json.items;
  return [];
}

const PAGE_TITLE_BAND_HEIGHT_PX = 100;
const headerInlineStyle: React.CSSProperties = {
  height: PAGE_TITLE_BAND_HEIGHT_PX,
  minHeight: PAGE_TITLE_BAND_HEIGHT_PX,
  padding: '0 64px 24px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
};

const PageContainer = styled(Box)({
  backgroundColor: '#fdffff',
  borderRadius: '12px',
  minHeight: '100%',
  height: 'auto',
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible',
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #99d4e1',
  boxShadow: 'inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)',
});

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(253, 255, 255, 0.2)',
  borderBottom: '1px solid #35d7e4',
  padding: '0px 64px 24px',
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  flexShrink: 0,
  boxSizing: 'border-box',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    padding: '0px 16px 16px',
    height: 'auto',
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  },
}));

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '16px 48px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  overflow: 'visible',
  boxSizing: 'border-box',
  minHeight: 'fit-content',
  [theme.breakpoints.down('md')]: {
    padding: '12px 16px',
  },
}));

const HeaderTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 500,
  color: '#262626',
  fontFamily: 'Rethink Sans, sans-serif',
  letterSpacing: '-1px',
  lineHeight: 1.5,
});

const HeaderIcon = styled(Box)({
  fontSize: '28px',
  color: '#065b70',
  display: 'flex',
  alignItems: 'center',
});

type DateRangeFilter = '' | '7' | '14' | '30';

const AUDIT_FILTER_MENU_Z_INDEX = 1400;
const auditFilterMenuProps = {
  style: { zIndex: AUDIT_FILTER_MENU_Z_INDEX },
  sx: { zIndex: AUDIT_FILTER_MENU_Z_INDEX },
  PaperProps: {
    sx: { zIndex: AUDIT_FILTER_MENU_Z_INDEX },
  },
};

const getDateRangeFromFilter = (range: DateRangeFilter) => {
  const now = new Date();
  const days = Number(range);
  return {
    start: format(subDays(now, days), 'yyyy-MM-dd'),
    end: format(now, 'yyyy-MM-dd'),
  };
};

const statusChipSx: Record<AuditActionStatus, { backgroundColor: string; color: string }> = {
  [AuditActionStatus.SUCCESS]: { backgroundColor: '#daf8ef', color: '#0b6f56' },
  [AuditActionStatus.FAILURE]: { backgroundColor: '#feeff2', color: '#9f2f45' },
  [AuditActionStatus.DENIED]: { backgroundColor: '#feeff2', color: '#9f2f45' },
  [AuditActionStatus.PARTIAL]: { backgroundColor: '#fff6df', color: '#8b5a11' },
};

const formatStatus = (status: AuditActionStatus) => {
  const v = status.toLowerCase();
  return `${v.charAt(0).toUpperCase()}${v.slice(1)}`;
};

function facilityIdString(facility: Record<string, unknown> | null | undefined): string {
  const rawId = facility?.facility_id ?? facility?.id;
  return rawId === null || rawId === undefined || rawId === ''
    ? ''
    : String(rawId);
}

function getInitialSelectedFacilityId(): string {
  const sessionFacility = getSelectedFacilityFromSession<Record<string, unknown>>();
  const sessionFacilityId = facilityIdString(sessionFacility);
  if (sessionFacilityId) {
    return sessionFacilityId;
  }

  try {
    const raw = localStorage.getItem('user');
    const parsed = raw ? (JSON.parse(raw) as { facilities?: Record<string, unknown>[] }) : null;
    const firstFacility = parsed?.facilities?.[0] ?? null;
    return facilityIdString(firstFacility);
  } catch {
    return '';
  }
}

/**
 * Audit Logs Dashboard
 *
 * Admin-only dashboard for viewing and managing all system audit logs.
 * Provides comprehensive search, filter, and export capabilities.
 *
 * HIPAA Compliance Features:
 * - View all audit logs (admin only)
 * - Advanced search and filtering
 * - Export audit logs for compliance reviews
 * - Real-time refresh
 * - Tamper-evident logging
 *
 * Security:
 * - This screen should be protected by role-based access control
 * - Only admins and compliance officers should have access
 * - All access to audit logs is itself logged (meta-auditing)
 */
const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [fetchToken, setFetchToken] = useState(0);
  const fetchLogsRef = useRef<() => Promise<void>>(async () => {});

  const [searchTerm, setSearchTerm] = useState('');
  const [filterActionType] = useState<AuditActionType | ''>('');
  const [filterResourceType, setFilterResourceType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<AuditActionStatus | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('');
  const [selectedFacility, setSelectedFacility] = useState<string>('');
  const [facilities, setFacilities] = useState<UserFacility[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(getSelectedCountryCode());
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const triggerFetch = (resetToFirstPage = false) => {
    if (resetToFirstPage && page !== 0) {
      setPage(0);
      return;
    }
    setFetchToken((prev) => prev + 1);
  };

  useEffect(() => {
    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = getSelectedFacilityFromSession();
      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: '/dashboard/audit-logs',
        method: 'GET',
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: 'Audit Logs',
          action: 'VIEW',
        },
        action_type: 'AUDIT_LOG_ACCESS',
        action_status: 'SUCCESS',
      });
    };
    logPageAccess();
  }, []);

  const buildFilterParams = (): AuditLogSearchParams => {
    const searchParams: AuditLogSearchParams = {
      sortBy: 'timestamp',
      sortOrder: 'desc',
    };

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) searchParams.searchTerm = trimmedSearch;
    if (filterActionType) searchParams.actionType = filterActionType as AuditActionType;
    if (filterResourceType) {
      const categoryResourceTypes = getResourceTypesByCategory(filterResourceType);
      if (categoryResourceTypes.length > 0) {
        searchParams.resourceType = categoryResourceTypes[0];
      }
    }
    if (filterStatus) searchParams.actionStatus = filterStatus as AuditActionStatus;
    if (startDate) searchParams.startDate = startDate;
    if (endDate) searchParams.endDate = endDate;
    if (selectedFacility) searchParams.facilityId = Number(selectedFacility);
    return searchParams;
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const searchParams: AuditLogSearchParams = {
        ...buildFilterParams(),
        page: page + 1,
        pageSize: rowsPerPage,
      };

      const response = await auditLogService.getAuditLogs(searchParams);
      setLogs(response.logs);
      setTotalLogs(response.total ?? 0);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  fetchLogsRef.current = fetchLogs;

  useEffect(() => {
    const loadFacilities = async () => {
      try {
        setLoadingFacilities(true);
        const raw = localStorage.getItem('user');
        const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
        const userId = (parsed?.['id'] as number) || (parsed?.['user_id'] as number);
        if (!userId) {
          return;
        }
        const fetched = await getFacilitiesForAuditLog(userId);
        setFacilities(fetched);
        setSelectedFacility((prev) =>
          prev &&
          fetched.some(
            (facility) => facilityIdString(facility as Record<string, unknown>) === prev,
          )
            ? prev
            : '',
        );
      } catch (e) {
        console.error('Failed to load facilities:', e);
      } finally {
        setLoadingFacilities(false);
      }
    };
    loadFacilities();
  }, [selectedCountry]);

  useEffect(() => {
    const checkCountryChange = () => {
      const current = getSelectedCountryCode();
      if (current !== selectedCountry) setSelectedCountry(current);
    };
    const intervalId = setInterval(checkCountryChange, 500);
    window.addEventListener('storage', checkCountryChange);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', checkCountryChange);
    };
  }, [selectedCountry]);

  useEffect(() => {
    fetchLogsRef.current();
  }, [page, rowsPerPage, fetchToken, selectedFacility]);

  useEffect(() => {
    const timeout = setTimeout(() => triggerFetch(true), 450);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const handleExport = async () => {
    try {
      const filters = buildFilterParams();
      const blob = await auditLogService.exportAuditLogs({
        format: 'csv',
        startDate: filters.startDate,
        endDate: filters.endDate,
        filters,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export audit logs');
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterResourceType('');
    setFilterStatus('');
    setDateRangeFilter('');
    setStartDate('');
    setEndDate('');
    setSelectedFacility('');
    triggerFetch(true);
  };

  const getActionDetails = (log: AuditLogEntry): Record<string, unknown> => {
    const raw = log.actionDetails;
    if (!raw) return {};
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
      } catch {
        return {};
      }
    }
    if (typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  const userRole = (log: AuditLogEntry) => {
    const details = getActionDetails(log);
    return (details.role as string) || (details.userRole as string) || 'User';
  };

  const userDisplayName = (log: AuditLogEntry) => {
    const details = getActionDetails(log);
    const detailsUser = (details.user as Record<string, unknown>) || {};
    const payloadUser = (details.user_info as Record<string, unknown>) || {};

    return (
      log.userName ||
      (details.userName as string) ||
      (details.user_name as string) ||
      (details.display_name as string) ||
      (detailsUser.userName as string) ||
      (detailsUser.user_name as string) ||
      (payloadUser.userName as string) ||
      (payloadUser.user_name as string) ||
      (log.userEmail ? log.userEmail.split('@')[0] : '') ||
      'Unknown'
    );
  };

  const facilityName = (log: AuditLogEntry) => {
    const details = getActionDetails(log);
    const parentFacilityName = details.parent_facility_name;
    const childFacilityName = details.child_facility_name;
    if (
      typeof parentFacilityName === 'string' &&
      parentFacilityName.trim() &&
      typeof childFacilityName === 'string' &&
      childFacilityName.trim() &&
      parentFacilityName !== childFacilityName
    ) {
      return `${parentFacilityName} / ${childFacilityName}`;
    }
    const detailsFacility = (details.facility_info as Record<string, unknown>) || {};
    const detailsFacilityAlt = (details.facility as Record<string, unknown>) || {};

    return (
      (details.facility_name as string) ||
      (details.facilityName as string) ||
      (detailsFacility.facility_name as string) ||
      (detailsFacility.facilityName as string) ||
      (detailsFacility.name as string) ||
      (detailsFacilityAlt.facility_name as string) ||
      (detailsFacilityAlt.facilityName as string) ||
      (detailsFacilityAlt.name as string) ||
      ((log.beforeValue as Record<string, unknown> | undefined)?.facility_name as string) ||
      ((log.afterValue as Record<string, unknown> | undefined)?.facility_name as string) ||
      log.resourceName ||
      'Unknown facility'
    );
  };

  const userInitials = (log: AuditLogEntry) => {
    const source = log.userName || log.userEmail || '';
    const parts = source.replace(/[@._-]/g, ' ').split(' ').filter(Boolean);
    if (parts.length === 0) return 'NA';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const activityTitle = (log: AuditLogEntry) => {
    const taken = getActionDetails(log).action_taken;
    if (typeof taken === 'string' && taken.trim()) return taken;
    if (log.actionType === AuditActionType.CREATE) return 'Created a record';
    if (log.actionType === AuditActionType.UPDATE) return `Tried to update ${log.resourceName || 'record'}`;
    if (
      log.actionType === AuditActionType.READ ||
      log.actionType === AuditActionType.VIEW ||
      log.actionType === AuditActionType.AUDIT_LOG_ACCESS
    ) {
      return 'Accessed audit logs';
    }
    if (log.actionType === AuditActionType.AUDIT_LOG_EXPORT || log.actionType === AuditActionType.DOWNLOAD) {
      return 'Exported compliance report';
    }
    return ACTION_TYPE_LABELS[log.actionType] || log.actionType;
  };

  const activitySubtitle = (log: AuditLogEntry) => {
    const resourceLabel = RESOURCE_TYPE_LABELS[log.resourceType] || log.resourceType;
    return `${resourceLabel} • ${facilityName(log)}`;
  };

  const totalPages = Math.max(1, Math.ceil((totalLogs || 0) / rowsPerPage));
  const currentPage = Math.min(page + 1, totalPages);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const handleRowsPerPageChange = (event: any) => {
    const value = Number(event.target.value);
    setRowsPerPage(value);
    setPage(0);
  };

  return (
    <PageContainer>
      <Header style={headerInlineStyle}>
        <HeaderLeft>
          <HeaderIcon>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path
                d="M14 2C7.37258 2 2 7.37258 2 14C2 20.6274 7.37258 26 14 26C20.6274 26 26 20.6274 26 14C26 7.37258 20.6274 2 14 2ZM14 4C19.5228 4 24 8.47715 24 14C24 19.5228 19.5228 24 14 24C8.47715 24 4 19.5228 4 14C4 8.47715 8.47715 4 14 4ZM13 8V10H15V8H13ZM13 12V20H15V12H13Z"
                fill="currentColor"
              />
            </svg>
          </HeaderIcon>
          <HeaderTitle>Audit Logs</HeaderTitle>
        </HeaderLeft>
      </Header>

      <ContentContainer>
        {/* Filter card */}
        <Paper
          elevation={0}
          sx={{
            p: '16px 20px 12px',
            backgroundColor: '#fff',
            border: '1px solid #e4e7ec',
            borderRadius: '12px',
          }}
        >
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by user or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#98a2b3' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: '14px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                '& fieldset': { borderColor: '#d0d5dd' },
                '&:hover fieldset': { borderColor: '#98a2b3' },
                '&.Mui-focused fieldset': { borderColor: '#35d7e4' },
              },
            }}
          />

          {/* Dropdowns row */}
          <Grid container spacing={1.5} alignItems="center">
            {/* Facility – teal pill style when a value is selected */}
            <Grid item xs={12} sm="auto">
              <TextField
                select
                size="small"
                value={selectedFacility}
                onChange={(e) => {
                  setSelectedFacility(e.target.value);
                  triggerFetch(true);
                }}
                disabled={loadingFacilities}
                SelectProps={{ displayEmpty: true, MenuProps: auditFilterMenuProps }}
                sx={{
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '14px',
                    fontWeight: selectedFacility ? 600 : 400,
                    borderRadius: '8px',
                    color: selectedFacility ? '#065b70' : '#344054',
                    '& fieldset': {
                      borderColor: selectedFacility ? '#0a829d' : '#d0d5dd',
                    },
                    '&:hover fieldset': { borderColor: '#35d7e4' },
                    '&.Mui-focused fieldset': { borderColor: '#35d7e4' },
                    backgroundColor: selectedFacility ? '#f0fdfe' : '#fff',
                  },
                }}
              >
                <MenuItem value="">All facilities</MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility.id} value={String(facility.id)}>
                    {formatFacilityDropdownLabel({
                      facility_name: facility.facility_name || `Facility ${facility.id}`,
                      city: facility.city,
                      state: facility.state,
                      address_1: facility.address_1,
                      address_2: facility.address_2,
                      full_address: facility.full_address,
                      country_code: facility.country_code,
                      ccn_number:
                        facility.npi_number ??
                        (facility as { ccn_number?: string }).ccn_number ??
                        (facility as { facility_unique_id?: string }).facility_unique_id,
                    })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm="auto">
              <TextField
                select
                size="small"
                value={filterResourceType}
                SelectProps={{ displayEmpty: true, MenuProps: auditFilterMenuProps }}
                onChange={(e) => {
                  setFilterResourceType(e.target.value as string);
                  triggerFetch(true);
                }}
                sx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '14px',
                    borderRadius: '8px',
                    color: '#344054',
                    '& fieldset': { borderColor: '#d0d5dd' },
                    '&:hover fieldset': { borderColor: '#98a2b3' },
                  },
                }}
              >
                <MenuItem value="">All screens</MenuItem>
                {Object.keys(RESOURCE_CATEGORY_LABELS).map((category) => (
                  <MenuItem key={category} value={category}>
                    {RESOURCE_CATEGORY_LABELS[category] || category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm="auto">
              <TextField
                select
                size="small"
                value={filterStatus}
                SelectProps={{ displayEmpty: true, MenuProps: auditFilterMenuProps }}
                onChange={(e) => {
                  setFilterStatus(e.target.value as AuditActionStatus);
                  triggerFetch(true);
                }}
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '14px',
                    borderRadius: '8px',
                    color: '#344054',
                    '& fieldset': { borderColor: '#d0d5dd' },
                    '&:hover fieldset': { borderColor: '#98a2b3' },
                  },
                }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {Object.values(AuditActionStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatStatus(status)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Vertical separator */}
            <Grid item sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Box sx={{ width: '1px', height: 28, backgroundColor: '#e4e7ec', mx: 0.5 }} />
            </Grid>

            <Grid item xs={12} sm="auto">
              <TextField
                select
                size="small"
                value={dateRangeFilter}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: auditFilterMenuProps,
                  renderValue: (selected) => {
                    if (!selected) {
                      return 'All time';
                    }
                    if (selected === '7') return 'Last 7 days';
                    if (selected === '14') return 'Last 14 days';
                    if (selected === '30') return 'Last 30 days';
                    return 'All time';
                  },
                }}
                onChange={(e) => {
                  const range = e.target.value as DateRangeFilter;
                  setDateRangeFilter(range);
                  if (range) {
                    const next = getDateRangeFromFilter(range);
                    setStartDate(next.start);
                    setEndDate(next.end);
                  } else {
                    setStartDate('');
                    setEndDate('');
                  }
                  triggerFetch(true);
                }}
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '14px',
                    borderRadius: '8px',
                    color: '#344054',
                    '& fieldset': { borderColor: '#d0d5dd' },
                    '&:hover fieldset': { borderColor: '#98a2b3' },
                  },
                }}
              >
                <MenuItem value="">All time</MenuItem>
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="14">Last 14 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm="auto" sx={{ ml: { sm: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon sx={{ fontSize: '16px !important' }} />}
                onClick={handleExport}
                disabled={loading || logs.length === 0}
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderColor: '#d0d5dd',
                  color: '#344054',
                  borderRadius: '8px',
                  px: 1.75,
                  py: 0.75,
                  '&:hover': { borderColor: '#98a2b3', backgroundColor: '#f8fafc' },
                }}
              >
                Export
              </Button>
            </Grid>
          </Grid>

          <Button
            onClick={handleResetFilters}
            sx={{
              mt: 1.25,
              px: 0,
              fontFamily: '"Rethink Sans", sans-serif',
              textTransform: 'none',
              fontSize: '13px',
              color: '#667085',
              '&:hover': { backgroundColor: 'transparent', color: '#344054' },
            }}
          >
            Reset filters
          </Button>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#0a829d' }} />
          </Box>
        )}

        {!loading && logs.length === 0 && !error && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontFamily: '"Rethink Sans", sans-serif', fontSize: '30px', color: '#101828' }}>
              No records found
            </Typography>
            <Typography sx={{ fontFamily: '"Rethink Sans", sans-serif', fontSize: '16px', color: '#667085' }}>
              Try adjusting filters and search keywords.
            </Typography>
          </Box>
        )}

        {!loading && logs.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #0a829d',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['User', 'Activity', 'Date & time', 'Status', ''].map((heading) => (
                      <TableCell
                        key={heading}
                        sx={{
                          backgroundColor: '#0a829d',
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#ffffff',
                          borderBottom: 'none',
                          py: 0.75,
                          px: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log, idx) => {
                    const isFailure =
                      log.actionStatus === AuditActionStatus.FAILURE ||
                      log.actionStatus === AuditActionStatus.DENIED;
                    return (
                      <TableRow
                        key={log.id || idx}
                        hover
                        onClick={() => {
                          setSelectedLog(log);
                          setIsDrawerOpen(true);
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f3fdff' },
                          '& td': { borderBottom: '1px solid #e5f5f8', py: 1, px: 1 },
                          // Failure rows get a left-edge accent like in the design
                          ...(isFailure && {
                            '& td:first-of-type': {
                              borderLeft: '3px solid #f04438',
                            },
                          }),
                        }}
                      >
                        {/* User */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor: idx % 2 ? '#f2f4f7' : '#e6fbf4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: '"Rethink Sans", sans-serif',
                                fontSize: '12px',
                                fontWeight: 700,
                                color: '#115e59',
                              }}
                            >
                              {userInitials(log)}
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  color: '#101828',
                                  lineHeight: 1.3,
                                }}
                              >
                              {userDisplayName(log)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: '"Rethink Sans", sans-serif',
                                  fontSize: '12px',
                                  color: '#667085',
                                  lineHeight: 1.3,
                                }}
                              >
                                {userRole(log)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Activity */}
                        <TableCell sx={{ py: 1.5, maxWidth: 320 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#101828',
                              lineHeight: 1.3,
                            }}
                          >
                            {activityTitle(log)}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: '12px',
                              color: '#667085',
                              lineHeight: 1.3,
                            }}
                          >
                            {activitySubtitle(log)}
                          </Typography>
                        </TableCell>

                        {/* Date & time */}
                        <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                          <Typography
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: '14px',
                              color: '#344054',
                            }}
                          >
                            {format(new Date(log.timestamp), 'MMM d, h:mm a')}
                          </Typography>
                        </TableCell>

                        {/* Status chip */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={formatStatus(log.actionStatus)}
                            size="small"
                            sx={{
                              fontFamily: '"Rethink Sans", sans-serif',
                              fontSize: '12px',
                              fontWeight: 600,
                              height: 24,
                              borderRadius: '999px',
                              ...statusChipSx[log.actionStatus],
                            }}
                          />
                        </TableCell>

                        {/* Chevron */}
                        <TableCell sx={{ py: 1.5, pr: 1.5, width: 32, textAlign: 'right' }}>
                          <ChevronRightIcon sx={{ fontSize: 20, color: '#98a2b3', display: 'block' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: '1px solid #e5f5f8',
                width: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                py: 0.75,
                px: 1.25,
              }}
            >
              <Typography
                sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: 12, color: '#065b70' }}
              >
                Total records: <strong>{totalLogs}</strong> | Showing{' '}
                {totalLogs === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}-
                {Math.min(currentPage * rowsPerPage, totalLogs)} of {totalLogs}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
                <Typography
                  sx={{ fontFamily: 'Rethink Sans, sans-serif', fontSize: 12, color: '#065b70' }}
                >
                  Rows per page
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  sx={{
                    minWidth: 80,
                    width: 80,
                    height: 28,
                    fontFamily: 'Rethink Sans, sans-serif',
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: 12,
                          minHeight: '28px',
                          lineHeight: '28px',
                          fontFamily: 'Rethink Sans, sans-serif',
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: 12,
                      height: 28,
                      minWidth: 28,
                      padding: '0 6px',
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: '#0a829d',
                      color: '#ffffff',
                    },
                    '& .MuiPaginationItem-root.Mui-selected:hover': { backgroundColor: '#0a829d' },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 420 },
              borderLeft: '1px solid #eaecf0',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Drawer header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Rethink Sans", sans-serif',
                fontSize: '17px',
                fontWeight: 600,
                color: '#101828',
              }}
            >
              Event detail
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsDrawerOpen(false)}
              sx={{
                border: '1px solid #e4e7ec',
                borderRadius: '8px',
                p: 0.5,
                color: '#667085',
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Divider />

          {/* Scrollable body */}
          {selectedLog && (
            <Box sx={{ overflowY: 'auto', flex: 1, px: 2.5, py: 2.5 }}>
              {/* Status + timestamp row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Chip
                  label={formatStatus(selectedLog.actionStatus)}
                  size="small"
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '12px',
                    fontWeight: 600,
                    height: 24,
                    borderRadius: '999px',
                    ...statusChipSx[selectedLog.actionStatus],
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: '13px',
                    color: '#475467',
                  }}
                >
                  {format(new Date(selectedLog.timestamp), "MMM d, yyyy '·' hh:mm:ss 'UTC'")}
                </Typography>
              </Box>

              {/* Section helper */}
              {[
                {
                  heading: 'USER',
                  rows: [
                    ['Name', userDisplayName(selectedLog)],
                    ['Email', selectedLog.userEmail || 'N/A'],
                    ['Role', userRole(selectedLog)],
                  ],
                },
                {
                  heading: 'ACTIVITY',
                  rows: [
                    ['Action', activityTitle(selectedLog)],
                    ['Screen', RESOURCE_TYPE_LABELS[selectedLog.resourceType] || selectedLog.resourceType],
                    ['Facility', facilityName(selectedLog)],
                  ],
                },
                {
                  heading: 'FIELD CHANGES',
                  rows: [
                    [
                      null,
                      selectedLog.beforeValue || selectedLog.afterValue
                        ? 'Changes captured'
                        : 'No changes recorded',
                    ],
                  ],
                },
                {
                  heading: 'TECHNICAL DETAILS',
                  rows: [
                    ['IP address', selectedLog.locationData?.ipAddress || 'N/A'],
                    [
                      'Device',
                      selectedLog.deviceInfo
                        ? `${selectedLog.deviceInfo.browser} ${selectedLog.deviceInfo.browserVersion} / ${selectedLog.deviceInfo.os}`
                        : 'N/A',
                    ],
                    ['Auth method', selectedLog.authenticationMethod || 'N/A'],
                    ['Session ID', selectedLog.sessionId || 'N/A'],
                  ],
                },
              ].map((section, sIdx) => (
                <Box key={section.heading} sx={{ mb: 2.5 }}>
                  {sIdx > 0 && <Divider sx={{ mb: 2 }} />}
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      color: '#98a2b3',
                      mb: 1.25,
                    }}
                  >
                    {section.heading}
                  </Typography>
                  {section.rows.map(([label, value]) => (
                    <Box
                      key={label ?? 'single'}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 0.6,
                        borderBottom: '1px solid #f2f4f7',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      {label && (
                        <Typography
                          sx={{
                            fontFamily: '"Rethink Sans", sans-serif',
                            fontSize: '14px',
                            color: '#667085',
                            minWidth: 90,
                            flexShrink: 0,
                          }}
                        >
                          {label}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: '14px',
                          color: '#101828',
                          textAlign: label ? 'right' : 'left',
                          wordBreak: 'break-all',
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Drawer>
      </ContentContainer>
    </PageContainer>
  );
};

export default AuditLogsPage;

